import React, { useEffect, useState } from 'react';
import {
    Card,
    Typography,
    Row,
    Col,
    Button,
    Form,
    InputNumber,
    Input,
    DatePicker,
    ConfigProvider,
    Table,
    Checkbox,
    Radio,
    Rate,
    Space,
    Select,
} from 'antd';
import {
    CalendarOutlined,
    SaveOutlined,
    ReloadOutlined,
    PlusOutlined,
    DeleteOutlined,
} from '@ant-design/icons';
import { useDispatch, useSelector } from 'react-redux';
import dayjs from 'dayjs';
import 'dayjs/locale/lt';
import locale from 'antd/locale/lt_LT';

import { AppDispatch, CombinedStates } from '../../core/types';
import { getConsultation, createOrUpdateConsultation } from '../../core/center-accreditation/centerAccreditationApi';

import './ScConsultation.scss';

// Set Lithuanian locale for dayjs
dayjs.locale('lt');

const { Text } = Typography;
const { TextArea } = Input;

// Available colors for consultation values
const availableColors = [
    { value: '#000000', label: 'Juoda', name: 'black' },
    { value: '#52c41a', label: 'Žalia', name: 'green' },
    { value: '#1890ff', label: 'Mėlyna', name: 'blue' },
    { value: '#f5222d', label: 'Raudona', name: 'red' },
    { value: '#722ed1', label: 'Violetinė', name: 'purple' },
    { value: '#fa8c16', label: 'Oranžinė', name: 'orange' },
    { value: '#eb2f96', label: 'Rožinė', name: 'pink' },
    { value: '#13c2c2', label: 'Cyan', name: 'cyan' },
];

// Default consultation areas
const defaultConsultationAreas = [
    { code: 'profesinis_orientavimas', title: '1. Profesinio orientavimo', values: [] },
    { code: 'socialine_pilietine', title: '2. Socialinė pilietinė veikla', values: [] },
    { code: 'savanoryste', title: '3. Savanorystė', values: [] },
    { code: 'sku_kalendorius', title: '4. SKU kalendoriaus pildymo', values: [] },
    { code: 'olimpiados_vykdymas', title: '5. Olimpiadų, konkursų vykdymas', values: [] },
    { code: 'olimpiados_vertinimas', title: '6. Olimpiadų, konkursų vertinimas', values: [] },
    { code: 'olimpiados_uzduotys', title: '7. Olimpiadų, konkursų užduočių rengimas', values: [] },
    { code: 'projektu_konsultavimas', title: '8. Projektų konsultavimas', values: [] },
    { code: 'kt_programos', title: '9. KT programų rengimas', values: [] },
    { code: 'programu_ekspertavimas', title: '10. Programų ekspertavimas', values: [] },
    { code: 'neformalus_suaugusiu', title: '11. Neformalusis suaugusiųjų švietimas', values: [] },
    { code: 'kt_renginiu_organizavimas', title: '12. KT renginių organizavimas', values: [] },
    { code: 'metodiniu_bureliu', title: '13. Metodinių būrelių veikla', values: [] },
];

// Default client groups stats (5.3)
const defaultClientGroupsStats = [
    { code: 'total', title: '5.3.1 Iš viso (jei manote, kad tikslinga, išskaidykite į grupes)', values: [] },
    { code: 'mokytojai', title: '5.3.2 Mokytojų', values: [] },
    { code: 'vadovai', title: '5.3.3 Mokyklų vadovų, jų pavaduotojų ugdymui, ugdymą organizuojančių skyrių vedėjų', values: [] },
    { code: 'pagalbos_spec', title: '5.3.4 Pagalbos specialistų', values: [] },
    { code: 'tevai', title: '5.3.5 Tėvų', values: [] },
    { code: 'kiti', title: '5.3.6 Kitų (įrašykite) verslo įmonių/įstaigų, organizacijų', values: [] },
];

// Default organizations stats (5.4)
const defaultOrganizationsStats = [
    { code: 'total', title: '5.4.1 Iš viso (jei manote, kad tikslinga, išskaidykite į grupes)', values: [] },
    { code: 'bendrojo_lavinimo', title: '5.4.2 Bendrojo lavinimo mokyklų', values: [] },
    { code: 'ikimokyklinio', title: '5.4.3 Ikimokyklinio ugdymo mokyklų', values: [] },
    { code: 'neformaliojo', title: '5.4.4 Neformaliojo vaikų švietimo mokyklų', values: [] },
    { code: 'kiti', title: '5.4.5 Kitų (įrašykite) verslo įmonių/įstaigų, organizacijų', values: [] },
];

// Helper component for dynamic value inputs with colors
interface DynamicValueInputProps {
    name: (string | number)[];
    label?: string;
    initialValues?: any[];
}

const DynamicValueInput = ({ name, label, initialValues = [] }: DynamicValueInputProps) => {
    const content = (
        <Form.List name={name}>
            {(fields: any[], { add, remove }: { add: (initialValue?: any) => void; remove: (fieldIndex: number) => void }) => (
                <div>
                    {fields.map((field: any) => {
                        const currentValue = initialValues[field.name] || { value: 0, color: '#000000' };
                        return (
                            <div key={field.key} style={{ marginBottom: 8, display: 'flex', gap: 8, alignItems: 'center' }}>
                                <Form.Item
                                    {...field}
                                    name={[field.name, 'value']}
                                    style={{ margin: 0, flex: 1 }}
                                    initialValue={currentValue.value}>
                                    <InputNumber
                                        min={0}
                                        style={{ width: '100%' }}
                                        placeholder="Skaičius" />
                                </Form.Item>
                                <Form.Item
                                    {...field}
                                    name={[field.name, 'color']}
                                    style={{ margin: 0, width: 120 }}
                                    initialValue={currentValue.color}>
                                    <Select
                                        style={{ width: '100%' }}
                                        placeholder="Spalva">
                                        {availableColors.map((color) => (
                                            <Select.Option key={color.value} value={color.value}>
                                                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                                    <span
                                                        style={{
                                                            display: 'inline-block',
                                                            width: 16,
                                                            height: 16,
                                                            backgroundColor: color.value,
                                                            border: '1px solid #d9d9d9',
                                                            borderRadius: 2,
                                                        }} />
                                                    <span>{color.label}</span>
                                                </div>
                                            </Select.Option>
                                        ))}
                                    </Select>
                                </Form.Item>
                                <Button
                                    type="text"
                                    danger
                                    style={{ marginTop: -25 }}
                                    icon={<DeleteOutlined />}
                                    onClick={() => remove(field.name)} />
                            </div>
                        );
                    })}
                    <Button
                        type="dashed"
                        icon={<PlusOutlined />}
                        onClick={() => add({ value: 0, color: '#000000' })}
                        block
                        style={{ marginTop: 8 }}>
                        Pridėti reikšmę
                    </Button>
                </div>
            )}
        </Form.List>
    );

    if (label) {
        return (
            <Form.Item label={label} noStyle={false}>
                {content}
            </Form.Item>
        );
    }
    return content;
};

DynamicValueInput.defaultProps = {
    label: undefined,
    initialValues: [],
};

const ScConsultation: React.FC = () => {
    const dispatch = useDispatch<AppDispatch>();
    const { consultation, isConsultationFetching } = useSelector((state: CombinedStates) => state.centerAccreditation);
    const { singleSchool } = useSelector((state: CombinedStates) => state.school);

    const [form] = Form.useForm();
    const [selectedDate, setSelectedDate] = useState<dayjs.Dayjs | null>(dayjs());

    useEffect(() => {
        dispatch(getConsultation({ date: selectedDate ? selectedDate.format('YYYY-MM-DD') : null }));
    }, [dispatch, selectedDate]);

    // Set form values when consultation data is loaded
    useEffect(() => {
        if (consultation) {
            // Merge default areas with existing data
            // If existing area has count, convert it to values array format
            const mergedAreas = defaultConsultationAreas.map((defaultArea) => {
                const existingArea = consultation.consultation_areas?.find(
                    (a: any) => a.code === defaultArea.code,
                );
                if (existingArea) {
                    // If existing area has values array, use it; otherwise convert count to values
                    if (existingArea.values && Array.isArray(existingArea.values)) {
                        return { ...defaultArea, values: existingArea.values };
                    }
                    if (existingArea.count !== undefined && existingArea.count > 0) {
                        // Convert old format (count) to new format (values array)
                        return {
                            ...defaultArea,
                            values: [{ value: existingArea.count, color: '#000000' }],
                        };
                    }
                }
                return defaultArea;
            });

            // Convert client_groups_stats to array format (like consultation_areas)
            const clientGroupsStats = consultation.client_groups_stats || {};
            const mergedClientGroupsStats = defaultClientGroupsStats.map((defaultStat) => {
                const existingStat = Array.isArray(clientGroupsStats)
                    ? clientGroupsStats.find((s: any) => s.code === defaultStat.code)
                    : clientGroupsStats[defaultStat.code];

                if (existingStat) {
                    // If existing stat has values array, use it
                    if (existingStat.values && Array.isArray(existingStat.values)) {
                        return { ...defaultStat, values: existingStat.values };
                    }
                    // If existing stat has count, convert it to values array
                    if (existingStat.count !== undefined && existingStat.count > 0) {
                        return {
                            ...defaultStat,
                            values: [{ value: existingStat.count, color: '#000000' }],
                        };
                    }
                    // If existing stat is a number or string, convert it to values array
                    let numValue = 0;
                    if (typeof existingStat === 'number') {
                        numValue = existingStat;
                    } else if (typeof existingStat === 'string') {
                        numValue = Number(existingStat) || 0;
                    }
                    if (numValue > 0) {
                        return {
                            ...defaultStat,
                            values: [{ value: numValue, color: '#000000' }],
                        };
                    }
                }
                return defaultStat;
            });

            // Convert consultation_topics to array format (like consultation_areas) for section 5.4
            // Note: consultation_topics is used to store organizations_stats data
            // API may return it as string[] or as array of objects (we'll treat it as array of objects)
            const consultationTopics: any = consultation.consultation_topics || [];
            const mergedConsultationTopics = defaultOrganizationsStats.map((defaultStat) => {
                // Check if consultationTopics is array of objects (new format) or strings (old format)
                let existingStat: any = null;
                if (Array.isArray(consultationTopics) && consultationTopics.length > 0) {
                    // Check if first element is an object (new format) or string (old format)
                    if (typeof consultationTopics[0] === 'object' && consultationTopics[0] !== null && !Array.isArray(consultationTopics[0])) {
                        existingStat = consultationTopics.find((s: any) => s && s.code === defaultStat.code);
                    }
                }

                if (existingStat) {
                    // If existing stat has values array, use it
                    if (existingStat.values && Array.isArray(existingStat.values)) {
                        return { ...defaultStat, values: existingStat.values };
                    }
                    // If existing stat has count, convert it to values array
                    if (existingStat.count !== undefined && existingStat.count > 0) {
                        return {
                            ...defaultStat,
                            values: [{ value: existingStat.count, color: '#000000' }],
                        };
                    }
                }
                return defaultStat;
            });

            form.setFieldsValue({
                consultation_areas: mergedAreas.length > 0 ? mergedAreas : defaultConsultationAreas,
                is_accounting_kept: consultation.is_accounting_kept,
                accounting_description: consultation.accounting_description || '',
                client_groups_stats: mergedClientGroupsStats,
                organizations_stats: defaultOrganizationsStats,
                consultation_forms: consultation.consultation_forms || [],
                consultation_frequency: consultation.consultation_frequency || '',
                has_effectiveness_criteria: consultation.has_effectiveness_criteria ?? null,
                consultation_topics: mergedConsultationTopics,
                consultant_invitees: consultation.consultant_requirements || [],
                consultant_requirements_ratings: consultation.consultant_requirements_ratings || {},
                consultations_trend: consultation.consultations_trend || null,
                usefulness_research_methods: consultation.usefulness_research_methods || [],
                usefulness_percent: consultation.usefulness_percent || null,
            });
        } else {
            // Reset to defaults if no data
            form.setFieldsValue({
                consultation_areas: defaultConsultationAreas,
                is_accounting_kept: false,
                accounting_description: '',
                client_groups_stats: defaultClientGroupsStats,
                organizations_stats: defaultOrganizationsStats,
                consultation_forms: [],
                consultation_frequency: '',
                has_effectiveness_criteria: null,
                consultation_topics: defaultOrganizationsStats,
                consultant_invitees: [],
                consultant_requirements_ratings: {},
                consultations_trend: null,
                usefulness_research_methods: [],
                usefulness_percent: null,
            });
        }
    }, [consultation, form]);

    const handleSave = async (values: any) => {
        // Transform consultation_areas to ensure all required fields are present
        // Calculate total count from values array
        const transformedAreas = (values.consultation_areas || defaultConsultationAreas).map((area: any) => {
            const valuesArray = area.values || [];
            const totalCount = valuesArray.reduce((sum: number, item: any) => sum + (item.value || 0), 0);
            return {
                code: area.code,
                title: area.title,
                count: totalCount,
                values: valuesArray, // Also send values array for future use
            };
        });

        // Combine consultant_requirements_ratings into consultant_requirements array
        // Based on API, consultant_requirements should be an array of strings
        // We'll include both the checkbox selections and the rated requirements
        const consultantInvitees = values.consultant_invitees || [];
        const consultantRequirementsRatings = values.consultant_requirements_ratings || {};

        // Convert ratings to requirements array - include requirements that have rating > 0
        const ratedRequirements = Object.keys(consultantRequirementsRatings).filter(
            (key) => consultantRequirementsRatings[key] > 0,
        );

        // Combine invitees and rated requirements
        const consultantRequirements = [...new Set([...consultantInvitees, ...ratedRequirements])];

        // Transform client_groups_stats to array format (like consultation_areas)
        const clientGroupsStats = values.client_groups_stats || defaultClientGroupsStats;
        const transformedClientGroupsStats = clientGroupsStats.map((stat: any) => {
            const valuesArray = stat.values || [];
            const totalCount = valuesArray.reduce((sum: number, item: any) => {
                if (item && typeof item === 'object' && 'value' in item) {
                    // Convert value to number (handles both string and number)
                    const numValue = typeof item.value === 'string' ? Number(item.value) : (item.value || 0);
                    return sum + (Number.isNaN(numValue) ? 0 : numValue);
                }
                return sum;
            }, 0);
            return {
                code: stat.code,
                title: stat.title,
                count: totalCount,
                values: valuesArray, // Also send values array for future use
            };
        });

        // Transform consultation_topics to array format (like consultation_areas) for section 5.4
        // Note: consultation_topics is used to store organizations_stats data
        const consultationTopics = values.consultation_topics || defaultOrganizationsStats;
        const transformedConsultationTopics = consultationTopics.map((stat: any) => {
            const valuesArray = stat.values || [];
            const totalCount = valuesArray.reduce((sum: number, item: any) => {
                if (item && typeof item === 'object' && 'value' in item) {
                    // Convert value to number (handles both string and number)
                    const numValue = typeof item.value === 'string' ? Number(item.value) : (item.value || 0);
                    return sum + (Number.isNaN(numValue) ? 0 : numValue);
                }
                return sum;
            }, 0);
            return {
                code: stat.code,
                title: stat.title,
                count: totalCount,
                values: valuesArray, // Also send values array for future use
            };
        });

        const requestData: any = {
            ...(consultation?.id && { id: consultation.id }),
            date: selectedDate ? selectedDate.format('YYYY-MM-DD') : null,
            consultation_areas: transformedAreas,
            is_accounting_kept: values.is_accounting_kept || false,
            accounting_description: values.accounting_description || null,
            client_groups_stats: transformedClientGroupsStats.length > 0 ? transformedClientGroupsStats : null,
            organizations_stats: null, // Not used anymore, data stored in consultation_topics
            consultation_forms: values.consultation_forms || [],
            consultation_frequency: values.consultation_frequency || null,
            has_effectiveness_criteria: values.has_effectiveness_criteria ?? null,
            consultation_topics: transformedConsultationTopics.length > 0 ? transformedConsultationTopics : [],
            consultant_requirements: consultantRequirements,
            consultant_requirements_ratings: consultantRequirementsRatings,
            consultations_trend: values.consultations_trend || null,
            usefulness_research_methods: values.usefulness_research_methods || [],
            usefulness_percent: values.usefulness_percent || null,
        };

        await dispatch(createOrUpdateConsultation(requestData));
    };

    const handleRefresh = (date?: dayjs.Dayjs) => {
        const dateToUse = date || selectedDate;
        dispatch(getConsultation({ date: dateToUse ? dateToUse.format('YYYY-MM-DD') : null }));
    };

    const handleRefreshClick = () => {
        handleRefresh();
    };

    // Consultation areas table columns
    const consultationAreasColumns = [
        {
            title: 'Sritys',
            dataIndex: 'title',
            key: 'title',
            width: '40%',
        },
        {
            title: 'Konsultacijų skaičius (KIEKVIENA ĮSIRAŠYTI KITA SPALVA)',
            dataIndex: 'values',
            key: 'values',
            width: '60%',
            render: (_values: any, record: any, index: number) => (
                <Form.Item
                    noStyle
                    shouldUpdate={(prevValues, currentValues) => {
                        const prevAreas = prevValues.consultation_areas || [];
                        const currAreas = currentValues.consultation_areas || [];
                        const prevAreaValues = prevAreas[index]?.values || [];
                        const currAreaValues = currAreas[index]?.values || [];
                        // Check only array length, not values themselves to avoid re-renders on input changes
                        const prevLength = Array.isArray(prevAreaValues) ? prevAreaValues.length : 0;
                        const currLength = Array.isArray(currAreaValues) ? currAreaValues.length : 0;
                        return prevLength !== currLength;
                    }}>
                    {({ getFieldValue }) => {
                        const allAreas = getFieldValue('consultation_areas') || [];
                        const currentArea = allAreas[index] || {};
                        const areaValues = Array.isArray(currentArea.values) ? currentArea.values : [];
                        return (
                            <DynamicValueInput
                                name={['consultation_areas', index, 'values']}
                                initialValues={areaValues} />
                        );
                    }}
                </Form.Item>
            ),
        },
    ];

    // Consultant requirements with rating
    const consultantRequirements = [
        { code: 'aptikti_priezastis', title: 'Moka aptikti probleminės situacijos priežastis', number: '5.6.1' },
        { code: 'igalinti_spręsti', title: 'Moka įgalinti patį žmogų ar organizaciją spręsti savo problemą', number: '5.6.2' },
        { code: 'aktyviai_dalyvauja', title: 'Konsultantas aktyviai dalyvauja sprendžiant problemą', number: '5.6.3' },
        { code: 'geba_bendrauti', title: 'Geba bendrauti, išklausyti', number: '5.6.4' },
        { code: 'kita_praktine', title: 'Kita (turi praktinės arba mokslinės tiriamosios patirties toje srityje)', number: '5.6.5' },
    ];

    return (
        <ConfigProvider locale={locale}>
            <div className="school-consultation">
                <Card title="Konsultavimas" className="consultation-form-card" loading={isConsultationFetching || false}>
                    <div style={{ marginBottom: 16, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <div>
                            {consultation?.period_description ? (
                                <Text strong style={{ fontSize: '16px' }}>
                                    <CalendarOutlined style={{ marginRight: 8 }} />
                                    {consultation.period_description}
                                </Text>
                            ) : (
                                <Text strong style={{ fontSize: '16px' }}>
                                    Pasirinktam laikotarpiui duomenys nepateikti, norėdami pateikti duomenis užpildykite ir išsaugokite formą.
                                </Text>
                            )}
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                            <DatePicker
                                placeholder="Pasirinkite datą"
                                value={selectedDate}
                                onChange={(date) => {
                                    const newDate = date || dayjs();
                                    setSelectedDate(newDate);
                                    handleRefresh(newDate);
                                }}
                                format="YYYY-MM-DD"
                                style={{ width: 180 }}
                                allowClear={false}
                                disabledDate={(current) => current && current > dayjs().endOf('day')} />
                            <Button
                                icon={<ReloadOutlined />}
                                onClick={handleRefreshClick}
                                loading={!!isConsultationFetching}>
                                Atnaujinti
                            </Button>
                        </div>
                    </div>
                    <Form
                        form={form}
                        layout="vertical"
                        onFinish={handleSave}
                        initialValues={{
                            consultation_areas: defaultConsultationAreas,
                            is_accounting_kept: false,
                            accounting_description: '',
                            client_groups_stats: defaultClientGroupsStats,
                            organizations_stats: defaultOrganizationsStats,
                            consultation_forms: [],
                            consultation_frequency: '',
                            consultation_topics: defaultOrganizationsStats,
                            consultant_invitees: [],
                            consultant_requirements_ratings: {},
                            consultations_trend: null,
                            usefulness_research_methods: [],
                            usefulness_percent: null,
                        }}>

                        {/* 5.1 Kokių sričių konsultacijas teikiate? */}
                        <Card title="5.1 Kokių sričių konsultacijas teikiate? (įrašykite skaičių)" style={{ marginBottom: 16 }}>
                            <Text type="secondary" style={{ display: 'block', marginBottom: 16, fontSize: '12px' }}>
                                (KIEKVIENA ĮSIRAŠYTI KITA SPALVA IŠ ASMENINIŲ KONSULTAVIMO ŽURNALŲ)
                            </Text>
                            <Form.Item name="consultation_areas" initialValue={defaultConsultationAreas}>
                                <Table
                                    columns={consultationAreasColumns}
                                    dataSource={defaultConsultationAreas}
                                    pagination={false}
                                    rowKey="code"
                                    bordered />
                            </Form.Item>
                        </Card>

                        {/* 5.2 Ar vedama konsultacijų apskaita? */}
                        <Card title="5.2 Ar vedama konsultacijų apskaita?" style={{ marginBottom: 16 }}>
                            <Form.Item name="is_accounting_kept" label="Apskaita">
                                <Radio.Group>
                                    <Radio value>5.2.1 Taip</Radio>
                                    <Radio value={false}>5.2.2 Ne</Radio>
                                </Radio.Group>
                            </Form.Item>
                            <Form.Item
                                noStyle
                                shouldUpdate={(prevValues, currentValues) => prevValues.is_accounting_kept !== currentValues.is_accounting_kept}>
                                {({ getFieldValue }) => (getFieldValue('is_accounting_kept') ? (
                                    <Form.Item name="accounting_description" label="Apskaitos aprašymas">
                                        <TextArea rows={3} placeholder="Įrašykite apskaitos aprašymą" />
                                    </Form.Item>
                                ) : null)}
                            </Form.Item>
                        </Card>

                        {/* 5.3 Kiek asmenų konsultuota per ataskaitinį laikotarpį? */}
                        <Form.Item
                            noStyle
                            shouldUpdate={(prevValues, currentValues) => prevValues.is_accounting_kept !== currentValues.is_accounting_kept}>
                            {({ getFieldValue }) => (getFieldValue('is_accounting_kept') ? (
                                <Card title="5.3 Kiek asmenų konsultuota per ataskaitinį laikotarpį? (įrašykite skaičių)" style={{ marginBottom: 16 }}>
                                    <Text type="secondary" style={{ display: 'block', marginBottom: 16, fontSize: '12px' }}>
                                        (KIEKVIENA ĮSIRAŠYTI KITA SPALVA IŠ ASMENINIŲ KONSULTAVIMO ŽURNALŲ)
                                    </Text>
                                    <Form.Item name="client_groups_stats" initialValue={defaultClientGroupsStats}>
                                        <Table
                                            columns={[
                                                {
                                                    title: 'Grupės',
                                                    dataIndex: 'title',
                                                    key: 'title',
                                                    width: '40%',
                                                },
                                                {
                                                    title: 'Konsultuotų asmenų skaičius (KIEKVIENA ĮSIRAŠYTI KITA SPALVA)',
                                                    dataIndex: 'values',
                                                    key: 'values',
                                                    width: '60%',
                                                    render: (_values: any, record: any, index: number) => (
                                                        <Form.Item
                                                            noStyle
                                                            shouldUpdate={(prevValues, currentValues) => {
                                                                const prevStats = prevValues.client_groups_stats || [];
                                                                const currStats = currentValues.client_groups_stats || [];
                                                                const prevStatValues = prevStats[index]?.values || [];
                                                                const currStatValues = currStats[index]?.values || [];
                                                                const prevLength = Array.isArray(prevStatValues) ? prevStatValues.length : 0;
                                                                const currLength = Array.isArray(currStatValues) ? currStatValues.length : 0;
                                                                return prevLength !== currLength;
                                                            }}>
                                                            {({ getFieldValue: getClientGroupsFieldValue }) => {
                                                                const allStats = getClientGroupsFieldValue('client_groups_stats') || [];
                                                                const currentStat = allStats[index] || {};
                                                                const statValues = Array.isArray(currentStat.values) ? currentStat.values : [];
                                                                return (
                                                                    <DynamicValueInput
                                                                        name={['client_groups_stats', index, 'values']}
                                                                        initialValues={statValues} />
                                                                );
                                                            }}
                                                        </Form.Item>
                                                    ),
                                                },
                                            ]}
                                            dataSource={defaultClientGroupsStats}
                                            pagination={false}
                                            rowKey="code"
                                            bordered />
                                    </Form.Item>
                                </Card>
                            ) : null)}
                        </Form.Item>

                        {/* 5.4 Kiek organizacijų konsultuota per ataskaitinį laikotarpį? */}
                        <Form.Item
                            noStyle
                            shouldUpdate={(prevValues, currentValues) => prevValues.is_accounting_kept !== currentValues.is_accounting_kept}>
                            {({ getFieldValue }) => (getFieldValue('is_accounting_kept') ? (
                                <Card title="5.4 Kiek organizacijų konsultuota per ataskaitinį laikotarpį? (įrašykite skaičių)" style={{ marginBottom: 16 }}>
                                    <Text type="secondary" style={{ display: 'block', marginBottom: 16, fontSize: '12px' }}>
                                        (KIEKVIENA ĮSIRAŠYTI KITA SPALVA IŠ ASMENINIŲ KONSULTAVIMO ŽURNALŲ)
                                    </Text>
                                    <Form.Item name="consultation_topics" initialValue={defaultOrganizationsStats}>
                                        <Table
                                            columns={[
                                                {
                                                    title: 'Organizacijos',
                                                    dataIndex: 'title',
                                                    key: 'title',
                                                    width: '40%',
                                                },
                                                {
                                                    title: 'Konsultuotų organizacijų skaičius (KIEKVIENA ĮSIRAŠYTI KITA SPALVA)',
                                                    dataIndex: 'values',
                                                    key: 'values',
                                                    width: '60%',
                                                    render: (_values: any, record: any, index: number) => (
                                                        <Form.Item
                                                            noStyle
                                                            shouldUpdate={(prevValues, currentValues) => {
                                                                const prevStats = prevValues.consultation_topics || [];
                                                                const currStats = currentValues.consultation_topics || [];
                                                                const prevStatValues = prevStats[index]?.values || [];
                                                                const currStatValues = currStats[index]?.values || [];
                                                                const prevLength = Array.isArray(prevStatValues) ? prevStatValues.length : 0;
                                                                const currLength = Array.isArray(currStatValues) ? currStatValues.length : 0;
                                                                return prevLength !== currLength;
                                                            }}>
                                                            {({ getFieldValue: getConsultationTopicsFieldValue }) => {
                                                                const allStats = getConsultationTopicsFieldValue('consultation_topics') || [];
                                                                const currentStat = allStats[index] || {};
                                                                const statValues = Array.isArray(currentStat.values) ? currentStat.values : [];
                                                                return (
                                                                    <DynamicValueInput
                                                                        name={['consultation_topics', index, 'values']}
                                                                        initialValues={statValues} />
                                                                );
                                                            }}
                                                        </Form.Item>
                                                    ),
                                                },
                                            ]}
                                            dataSource={defaultOrganizationsStats}
                                            pagination={false}
                                            rowKey="code"
                                            bordered />
                                    </Form.Item>
                                </Card>
                            ) : null)}
                        </Form.Item>

                        {/* 5.5 Ką kviečiate konsultantais? */}
                        <Card title="5.5 Ką kviečiate konsultantais?" style={{ marginBottom: 16 }}>
                            <Form.Item name="consultant_invitees" label="Konsultantai">
                                <Checkbox.Group>
                                    <Row>
                                        <Col span={24}>
                                            <Checkbox value="institucijos_darbuotojai">5.5.1 Institucijos darbuotojus</Checkbox>
                                        </Col>
                                        <Col span={24}>
                                            <Checkbox value="mokytojai_praktikai">5.5.2 Mokytojus praktikus</Checkbox>
                                        </Col>
                                        <Col span={24}>
                                            <Checkbox value="aukstuju_mokyklu_mokslininkai">5.5.3 Aukštųjų mokyklų mokslininkus, dėstytojus</Checkbox>
                                        </Col>
                                        <Col span={24}>
                                            <Checkbox value="uzsienio_mokslininkai">5.5.4 Užsienio šalių mokslininkus</Checkbox>
                                        </Col>
                                        <Col span={24}>
                                            <Checkbox value="savivaldybiu_darbuotojai">5.5.5 Savivaldybių administracijų švietimo padalinių darbuotojus</Checkbox>
                                        </Col>
                                        <Col span={24}>
                                            <Checkbox value="mokyklu_vadovai">5.5.6 Mokyklų vadovai, jų pavaduotojai</Checkbox>
                                        </Col>
                                    </Row>
                                </Checkbox.Group>
                            </Form.Item>
                        </Card>

                        {/* 5.6 Kokius reikalavimus keliate konsultantui? */}
                        <Card title="5.6 Kokius reikalavimus keliate konsultantui?" style={{ marginBottom: 16 }}>
                            <Text type="secondary" style={{ display: 'block', marginBottom: 16 }}>
                                Įvertinkite balais nuo 0 iki 4 (4 – svarbiausia)
                            </Text>
                            <Form.Item name="consultant_requirements_ratings">
                                <Space direction="vertical" style={{ width: '100%' }}>
                                    {consultantRequirements.map((req) => (
                                        <Row key={req.code} gutter={16} align="middle">
                                            <Col span={16}>
                                                <Text>{req.number} {req.title}</Text>
                                            </Col>
                                            <Col span={8}>
                                                <Form.Item
                                                    name={['consultant_requirements_ratings', req.code]}
                                                    style={{ margin: 0 }}
                                                    initialValue={0}>
                                                    <Rate count={4} />
                                                </Form.Item>
                                            </Col>
                                        </Row>
                                    ))}
                                </Space>
                            </Form.Item>
                        </Card>

                        {/* 5.7 Kada teikiamos konsultacijos? */}
                        <Card title="5.7 Kada teikiamos konsultacijos? (pažymėkite tipiškiausią atvejį)" style={{ marginBottom: 16 }}>
                            <Form.Item name="consultation_forms" label="Konsultacijų laikas">
                                <Checkbox.Group>
                                    <Row>
                                        <Col span={24}>
                                            <Checkbox value="kliento_pageidaujamu">5.7.1 Kliento pageidaujamu laiku</Checkbox>
                                        </Col>
                                        <Col span={24}>
                                            <Checkbox value="institucijos_nustatytu">5.7.2 Institucijos nustatytu laiku</Checkbox>
                                        </Col>
                                        <Col span={24}>
                                            <Checkbox value="salims_susitarus">5.7.3 Šalims susitarus</Checkbox>
                                        </Col>
                                        <Col span={24}>
                                            <Checkbox value="kitu_laiku">5.7.4 Kitu laiku (įrašykite)</Checkbox>
                                        </Col>
                                    </Row>
                                </Checkbox.Group>
                            </Form.Item>
                            <Form.Item
                                noStyle
                                shouldUpdate={(prevValues, currentValues) => {
                                    const prev = prevValues.consultation_forms || [];
                                    const curr = currentValues.consultation_forms || [];
                                    return prev.includes('kitu_laiku') !== curr.includes('kitu_laiku');
                                }}>
                                {({ getFieldValue }) => {
                                    const forms = getFieldValue('consultation_forms') || [];
                                    return forms.includes('kitu_laiku') ? (
                                        <Form.Item name="consultation_frequency" label="Kitas laikas">
                                            <Input placeholder="Įrašykite kitą laiką" />
                                        </Form.Item>
                                    ) : null;
                                }}
                            </Form.Item>
                        </Card>

                        {/* 5.8 Ar esate parengę konsultacijų efektyvumo vertinimo kriterijus? */}
                        <Card title="5.8 Ar esate parengę konsultacijų efektyvumo vertinimo kriterijus?" style={{ marginBottom: 16 }}>
                            <Form.Item name="has_effectiveness_criteria" label="Efektyvumo kriterijai">
                                <Radio.Group>
                                    <Radio value>5.8.1 Taip</Radio>
                                    <Radio value={false}>5.8.2 Ne</Radio>
                                </Radio.Group>
                            </Form.Item>
                        </Card>

                        {/* 5.9 Kaip kinta konsultacijų skaičius? */}
                        <Card title="5.9 Kaip kinta konsultacijų skaičius?" style={{ marginBottom: 16 }}>
                            <Form.Item name="consultations_trend" label="Tendencija">
                                <Radio.Group>
                                    <Radio value="dideja">5.9.1 Didėja</Radio>
                                    <Radio value="mazeja">5.9.2 Mažėja</Radio>
                                    <Radio value="nekinta">5.9.3 Nekinta</Radio>
                                </Radio.Group>
                            </Form.Item>
                        </Card>

                        {/* 5.10 Kiek apytiksliai klientų nurodo, jog konsultacijos duoda naudos? */}
                        <Card title="5.10 Kiek apytiksliai klientų nurodo, jog konsultacijos duoda naudos? (nurodykite procentais)" style={{ marginBottom: 16 }}>
                            <Form.Item name="usefulness_percent" label="Naudingumo procentas">
                                <InputNumber
                                    min={0}
                                    max={100}
                                    formatter={(value) => `${value}%`}
                                    style={{ width: '100%' }}
                                    placeholder="Įrašykite procentą (0-100)" />
                            </Form.Item>
                        </Card>

                        {/* Form Actions */}
                        <div style={{ textAlign: 'right', marginTop: 24 }}>
                            <Button type="primary" htmlType="submit" icon={<SaveOutlined />}>
                                Išsaugoti
                            </Button>
                        </div>
                    </Form>
                </Card>
            </div>
        </ConfigProvider>
    );
};

export default ScConsultation;
